// Typed HTTP client for the backend, with two safety nets:
//
//   1. Automatic retry on 5xx / 429 with 200/400/800 ms backoff.
//   2. Auto-fallback to client-side `demoData` when no backend is reachable.
//      A one-shot probe at first call decides this; for static deploys you
//      can force demo mode at build time with `VITE_DEMO_MODE=true`.
//
// Everything UI-facing imports from here, never `fetch` directly.

import type {
  ApiError,
  ArtistSearchResult,
  BookingUrls,
  SearchRequest,
  SearchResponse,
  ShowWithPackages,
} from "../types";
import {
  autocompleteDemo,
  demoBookingUrls,
  runDemoSearch,
} from "./demoData";

const BASE = "/v1";

const RETRY_DELAYS_MS = [200, 400, 800];

// Forced demo build (static deploy with no backend): VITE_DEMO_MODE=true.
const FORCE_DEMO =
  (import.meta.env.VITE_DEMO_MODE as string | undefined) === "true";

// Lazily memoized backend probe. If the API isn't reachable (no backend
// running, or a static demo deploy) we transparently fall back to the
// client-side demo engine so the site always works.
let backendProbe: Promise<boolean> | null = null;

function probeBackend(): Promise<boolean> {
  if (FORCE_DEMO) return Promise.resolve(false);
  if (!backendProbe) {
    backendProbe = (async () => {
      try {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 1500);
        const res = await fetch(`${BASE}/artists/search?q=a`, {
          signal: ctrl.signal,
        });
        clearTimeout(timer);
        // A real backend returns JSON here; a static host returns HTML/404.
        const type = res.headers.get("content-type") ?? "";
        return res.ok && type.includes("application/json");
      } catch {
        return false;
      }
    })();
  }
  return backendProbe;
}

/** True when the app is serving client-side demo data (no live backend). */
export async function isDemoMode(): Promise<boolean> {
  return !(await probeBackend());
}

function shouldRetry(status: number): boolean {
  return status >= 500 || status === 429;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let lastError: ApiError | null = null;

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${BASE}${path}`, {
        headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
        ...init,
      });
    } catch (networkErr) {
      lastError = {
        error:
          networkErr instanceof Error ? networkErr.message : "Network error",
        code: "NETWORK_ERROR",
        statusCode: 0,
      };
      if (attempt < RETRY_DELAYS_MS.length) {
        await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
        continue;
      }
      throw lastError;
    }

    if (res.ok) {
      return res.json() as Promise<T>;
    }

    let body: ApiError;
    try {
      body = (await res.json()) as ApiError;
    } catch {
      body = {
        error: `Request failed with status ${res.status}`,
        code: "HTTP_ERROR",
        statusCode: res.status,
      };
    }

    if (shouldRetry(res.status) && attempt < RETRY_DELAYS_MS.length) {
      lastError = body;
      await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
      continue;
    }

    throw body;
  }

  throw lastError ?? {
    error: "Unknown error",
    code: "UNKNOWN",
    statusCode: 0,
  };
}

export async function searchShows(req: SearchRequest): Promise<SearchResponse> {
  if (!(await probeBackend())) return runDemoSearch(req);
  return request<SearchResponse>("/search", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

export async function searchArtists(
  q: string,
): Promise<{ artists: ArtistSearchResult[] }> {
  if (!(await probeBackend())) return { artists: autocompleteDemo(q) };
  return request<{ artists: ArtistSearchResult[] }>(
    `/artists/search?q=${encodeURIComponent(q)}`,
  );
}

export async function getShowDetail(showId: string): Promise<ShowWithPackages> {
  return request<ShowWithPackages>(`/shows/${showId}/detail`);
}

export async function getBookingUrls(showId: string): Promise<BookingUrls> {
  if (!(await probeBackend())) return demoBookingUrls();
  return request<BookingUrls>(`/shows/${showId}/book`);
}
